import apiClient, * as Config from '@/configs/config'

// Portal API AIChatController:
//   POST {VITE_API_URL}/api/chat        { agentId, message, conversationId? }
//                                       -> { reply, conversationId }
//   POST {VITE_API_URL}/api/chat/stream same body -> Server-Sent Events:
//                                       meta {conversationId}, token {content}
//                                       (repeated), then done {} | error {message}
//   POST {VITE_API_URL}/api/chat/reset  { agentId, conversationId }
// The caller is identified by the session: these calls send the logged-in
// user's own JWT (localStorage 'token'), not the shared static token, and the
// API reads the user from it. The server keeps the conversation's recent turns
// keyed by conversationId (omit it to start a new one). agentId selects an
// active aia_agent row and comes from VITE_AI_AGENT_ID.

// On/off switch for the whole widget. VITE_HR_AI_ASST_ENABLED in the .env.*
// file wins when set ('true' shows, 'false' hides) - handy for testing without
// touching the database. When it is not set, the server-side app config row
// decides: it comes back as responseContent[0].app_value and only the string
// 'true' shows the widget. Resolved once per page load and shared by every caller.
let enabledFlagPromise: Promise<boolean> | null = null

const envEnabledOverride = (): boolean | null => {
    const raw = String(import.meta.env.VITE_HR_AI_ASST_ENABLED ?? '')
        .trim()
        .toLowerCase()
    if (raw === 'true') return true
    if (raw === 'false') return false
    return null
}

export const fetchHrAiAssistEnabled = (): Promise<boolean> => {
    const override = envEnabledOverride()
    if (override !== null) return Promise.resolve(override)

    if (!enabledFlagPromise) {
        enabledFlagPromise = apiClient
            .post('/customdata/getdata', {
                dataCode: 'GET_HR_AI_ASST_ENABLED_FLAG',
                placeholderKeyValueMap: {},
            })
            .then((response) => {
                const value = response?.data?.responseContent?.[0]?.app_value
                return String(value).trim().toLowerCase() === 'true'
            })
            .catch((error) => {
                // Fail closed: if the flag can't be read, keep the widget hidden.
                console.error('Failed to fetch HR AI assistant flag', error)
                return false
            })
    }
    return enabledFlagPromise
}

export type AiChatButton = { title: string; payload?: string }

export type AiBotReply = { text: string; buttons?: AiChatButton[] }

export type AiChatResult = {
    replies: AiBotReply[]
    conversationId: string | null
}

/** Agent id not configured for this build. */
export class AiChatConfigError extends Error {}

/** No user session, or the API rejected it (expired/invalid token). */
export class AiChatAuthError extends Error {}

const agentId = (): number | null => {
    const raw = import.meta.env.VITE_AI_AGENT_ID
    const id = raw ? Number(raw) : NaN
    return Number.isFinite(id) && id > 0 ? id : null
}

const userSessionHeaders = () => {
    const token = localStorage.getItem('token')
    if (!token) {
        throw new AiChatAuthError('No user session token')
    }
    return {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
    }
}

const requireAgentId = (): number => {
    const agent = agentId()
    if (agent === null) {
        throw new AiChatConfigError('VITE_AI_AGENT_ID is not configured')
    }
    return agent
}

export const sendAiChatMessage = async (
    message: string,
    conversationId: string | null,
): Promise<AiChatResult> => {
    const agent = requireAgentId()

    const resp = await fetch(`${Config.apiUrl}/api/chat`, {
        method: 'POST',
        headers: userSessionHeaders(),
        body: JSON.stringify({
            agentId: agent,
            message,
            ...(conversationId ? { conversationId } : {}),
        }),
    })

    if (resp.status === 401 || resp.status === 403) {
        throw new AiChatAuthError(
            `Chat API rejected the session: ${resp.status}`,
        )
    }
    if (!resp.ok) {
        throw new Error(`Chat API error: ${resp.status}`)
    }

    const data = await resp.json()
    const reply = (data?.reply ?? '').toString().trim()
    return {
        replies: reply ? [{ text: reply }] : [],
        conversationId: data?.conversationId ?? conversationId,
    }
}

/** The API accepted the request but the model failed mid-reply. */
export class AiChatStreamError extends Error {}

export type AiChatStreamHandlers = {
    /** Called once, before any token, with the server's conversation id. */
    onConversationId?: (conversationId: string) => void
    /** Called for each piece of reply text, in order; append them. */
    onToken: (text: string) => void
    signal?: AbortSignal
}

// One SSE event block ("event:x\ndata:y") -> name + JSON payload. Multiple
// data lines are joined with "\n" per the SSE spec.
const parseSseEvent = (block: string) => {
    let name = 'message'
    const data: string[] = []
    for (const line of block.split(/\r?\n/)) {
        if (line.startsWith('event:')) name = line.slice(6).trim()
        else if (line.startsWith('data:')) data.push(line.slice(5))
    }
    if (!data.length) return null
    try {
        return { name, payload: JSON.parse(data.join('\n')) }
    } catch {
        return null
    }
}

/**
 * Streams the reply as the model generates it (fetch + stream reader, since
 * EventSource can't POST or send the Authorization header). Resolves with the
 * conversation id once the server sends "done".
 */
export const streamAiChatMessage = async (
    message: string,
    conversationId: string | null,
    handlers: AiChatStreamHandlers,
): Promise<{ conversationId: string | null }> => {
    const agent = requireAgentId()

    const resp = await fetch(`${Config.apiUrl}/api/chat/stream`, {
        method: 'POST',
        headers: { ...userSessionHeaders(), Accept: 'text/event-stream' },
        body: JSON.stringify({
            agentId: agent,
            message,
            ...(conversationId ? { conversationId } : {}),
        }),
        signal: handlers.signal,
    })
    return readChatStream(resp, conversationId, handlers)
}

// Files the chat accepts; the API checks the same rules again (type from the
// file's first bytes, not just its name).
export const AI_ATTACHMENT_ACCEPT = '.xlsx,.pdf,.png,.jpg,.jpeg'
export const AI_ATTACHMENT_MAX_BYTES = 2 * 1024 * 1024
const AI_ATTACHMENT_EXTENSIONS = ['xlsx', 'pdf', 'png', 'jpg', 'jpeg']

/** Why the file can't be attached, or null when it can. */
export const checkAiAttachment = (file: File): string | null => {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? ''
    if (!AI_ATTACHMENT_EXTENSIONS.includes(extension)) {
        return 'Only .xlsx, .pdf, .png, .jpg and .jpeg files can be attached.'
    }
    if (file.size === 0) return 'The file is empty.'
    if (file.size > AI_ATTACHMENT_MAX_BYTES) {
        return 'The file is larger than 2 MB.'
    }
    return null
}

/** The API refused the request with a reason meant for the user. */
export class AiChatRequestError extends Error {}

/**
 * streamAiChatMessage with a file: POST {VITE_API_URL}/api/chat/stream-with-file
 * (multipart). The API reads the file - scanned pages and images through OCR
 * - and answers with the same event stream. A file it can't use is refused
 * before streaming with {"message": "..."}, raised as AiChatRequestError.
 */
export const streamAiChatMessageWithFile = async (
    message: string,
    file: File,
    conversationId: string | null,
    handlers: AiChatStreamHandlers,
): Promise<{ conversationId: string | null }> => {
    const agent = requireAgentId()
    const token = localStorage.getItem('token')
    if (!token) throw new AiChatAuthError('No user session token')

    const form = new FormData()
    form.append('agentId', String(agent))
    form.append('message', message)
    if (conversationId) form.append('conversationId', conversationId)
    form.append('file', file)

    // No Content-Type header: the browser sets multipart with its boundary.
    const resp = await fetch(`${Config.apiUrl}/api/chat/stream-with-file`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
        },
        body: form,
        signal: handlers.signal,
    })
    if (!resp.ok && resp.status !== 401 && resp.status !== 403) {
        const body = await resp.json().catch(() => null)
        if (body?.message) throw new AiChatRequestError(String(body.message))
        if (resp.status === 413) {
            throw new AiChatRequestError('The file is larger than 2 MB.')
        }
    }
    return readChatStream(resp, conversationId, handlers)
}

/** Reads the chat event stream: meta, token..., then done or error. */
const readChatStream = async (
    resp: Response,
    conversationId: string | null,
    { onConversationId, onToken }: AiChatStreamHandlers,
): Promise<{ conversationId: string | null }> => {
    if (resp.status === 401 || resp.status === 403) {
        throw new AiChatAuthError(
            `Chat API rejected the session: ${resp.status}`,
        )
    }
    if (!resp.ok || !resp.body) {
        throw new Error(`Chat API error: ${resp.status}`)
    }

    let currentId = conversationId
    let buffer = ''
    const reader = resp.body.pipeThrough(new TextDecoderStream()).getReader()

    try {
        for (;;) {
            const { value, done } = await reader.read()
            if (done) break
            buffer += value

            // Events end with a blank line; keep any incomplete tail.
            const blocks = buffer.split(/\r?\n\r?\n/)
            buffer = blocks.pop() ?? ''

            for (const block of blocks) {
                const event = parseSseEvent(block)
                if (!event) continue
                switch (event.name) {
                    case 'meta':
                        if (event.payload?.conversationId) {
                            currentId = event.payload.conversationId
                            onConversationId?.(event.payload.conversationId)
                        }
                        break
                    case 'token':
                        if (event.payload?.content) {
                            onToken(String(event.payload.content))
                        }
                        break
                    case 'error':
                        throw new AiChatStreamError(
                            event.payload?.message ?? 'Chat stream failed',
                        )
                    case 'done':
                        return { conversationId: currentId }
                }
            }
        }
    } finally {
        reader.cancel().catch(() => undefined)
    }

    // Connection closed without "done" (proxy timeout, server restart).
    throw new AiChatStreamError('Chat stream ended unexpectedly')
}

// Best effort: the conversation is dropped locally either way.
export const resetAiChat = async (conversationId: string): Promise<void> => {
    const agent = agentId()
    if (agent === null) return

    const resp = await fetch(`${Config.apiUrl}/api/chat/reset`, {
        method: 'POST',
        headers: userSessionHeaders(),
        body: JSON.stringify({ agentId: agent, conversationId }),
    })
    if (!resp.ok) {
        throw new Error(`Chat reset error: ${resp.status}`)
    }
}

/** The server can't speak this text; use the browser's voice instead. */
export class AiSpeechUnavailableError extends Error {}

// POST {VITE_API_URL}/api/chat/speech - a piece of a reply as WAV audio, from
// the local speech engines (Kokoro / AI4Bharat Indic) behind the API. 503 when
// speech is not set up or there is no voice for the text's language.
export const fetchAiSpeech = async (
    text: string,
    signal: AbortSignal,
): Promise<Blob> => {
    const resp = await fetch(`${Config.apiUrl}/api/chat/speech`, {
        method: 'POST',
        headers: { ...userSessionHeaders(), Accept: 'audio/wav' },
        body: JSON.stringify({ text }),
        signal,
    })
    if (!resp.ok) {
        const body = await resp.json().catch(() => null)
        throw new AiSpeechUnavailableError(
            body?.message ?? `Speech error: ${resp.status}`,
        )
    }
    return resp.blob()
}

export type AiReplyRating = 'GOOD' | 'BAD'

// POST {VITE_API_URL}/api/chat/feedback - thumbs up / down on one reply. The
// question and reply are stored with the rating for HR to review; rating the
// same reply (messageRef) again replaces the earlier rating.
export const submitAiFeedback = async (feedback: {
    messageRef: string
    rating: AiReplyRating
    conversationId: string | null
    question: string
    reply: string
}): Promise<void> => {
    const resp = await fetch(`${Config.apiUrl}/api/chat/feedback`, {
        method: 'POST',
        headers: userSessionHeaders(),
        body: JSON.stringify({ agentId: agentId(), ...feedback }),
    })
    if (resp.status === 401 || resp.status === 403) {
        throw new AiChatAuthError(`Feedback rejected (${resp.status})`)
    }
    if (!resp.ok) {
        throw new Error(`Chat feedback error: ${resp.status}`)
    }
}

// Files offered in answers (e.g. the full employee list as a PDF, when it is
// too long for the chat) are served from /api/chat/exports/<token>. That needs
// the user's own JWT, which a plain link can't send, so the widget downloads
// them through here instead of following the link.
const AI_EXPORT_PATH = /^\/api\/chat\/exports\/[A-Za-z0-9-]+$/

export const isAiExportLink = (href?: string): href is string =>
    !!href && AI_EXPORT_PATH.test(href)

// An export path the model wrote as text - bare, in `code`, or in (brackets) -
// instead of as a markdown link. Group 1 is the path.
const LOOSE_EXPORT_PATH =
    /\(?\s*`?(\/api\/chat\/exports\/[A-Za-z0-9-]+)`?\s*\)?/g

/**
 * Turns every export path in a reply into a markdown link, so it renders as
 * the download button however the model wrote it. Paths that already are a
 * link's target - "[text](/api/chat/exports/...)" - are left alone.
 */
export const linkifyAiExports = (markdown: string): string =>
    markdown.replace(
        LOOSE_EXPORT_PATH,
        (match: string, path: string, offset: number, full: string) => {
            const before = full.slice(0, offset)
            const isLinkTarget = match.startsWith('(')
                ? before.endsWith(']')
                : before.endsWith('](')
            return isLinkTarget ? match : ` [Download full list (PDF)](${path})`
        },
    )

export const downloadAiExport = async (path: string): Promise<void> => {
    if (!isAiExportLink(path)) throw new Error('Not a download link.')
    const token = localStorage.getItem('token')
    if (!token) throw new AiChatAuthError('No user session token')

    const resp = await fetch(`${Config.apiUrl}${path}`, {
        headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/pdf',
        },
    })
    if (resp.status === 401 || resp.status === 403) {
        throw new AiChatAuthError(`Download rejected (${resp.status})`)
    }
    if (!resp.ok) {
        throw new Error(
            resp.status === 404
                ? 'This download link has expired. Ask the assistant for the list again.'
                : 'The download failed. Please try again.',
        )
    }

    // The file name is only readable when the API exposes the header (CORS).
    const fileName =
        /filename="?([^";]+)"?/.exec(
            resp.headers.get('Content-Disposition') ?? '',
        )?.[1] ?? 'employee-list.pdf'
    const url = URL.createObjectURL(await resp.blob())
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
}
